import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  MfaSetupResponse,
  MfaVerifyRequest,
  RefreshTokenRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  OAuth2LoginRequest
} from '../models/auth.model';
import { User } from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = `${environment.apiUrl}/auth`;
  private readonly TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'current_user';

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  // Initialize auth state from storage
  initializeAuth(): void {
    const token = this.getToken();
    const user = this.getStoredUser();
    console.log('[AuthService] Initializing auth...', { hasToken: !!token, hasUser: !!user });
    if (token && user) {
      console.log('[AuthService] User authenticated:', user.username);
      this.currentUserSubject.next(user);
      this.isAuthenticatedSubject.next(true);
      this.scheduleTokenRefresh();
    } else {
      console.log('[AuthService] No valid session found');
      this.isAuthenticatedSubject.next(false);
    }
  }

  // Login with username/password
  login(request: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/login`, request)
      .pipe(
        tap(response => this.handleAuthSuccess(response)),
        catchError(error => this.handleAuthError(error))
      );
  }

  // Register new user
  register(request: RegisterRequest): Observable<any> {
    return this.http.post(`${this.API_URL}/register`, request);
  }

  // OAuth2 login (Google, Azure)
  oauth2Login(request: OAuth2LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/oauth2/callback`, request)
      .pipe(
        tap(response => this.handleAuthSuccess(response)),
        catchError(error => this.handleAuthError(error))
      );
  }

  // Setup MFA
  setupMfa(): Observable<MfaSetupResponse> {
    return this.http.post<MfaSetupResponse>(`${this.API_URL}/mfa/setup`, {});
  }

  // Verify MFA
  verifyMfa(request: MfaVerifyRequest): Observable<any> {
    return this.http.post(`${this.API_URL}/mfa/verify`, request);
  }

  // Disable MFA
  disableMfa(totpCode: string): Observable<any> {
    return this.http.post(`${this.API_URL}/mfa/disable`, { totpCode });
  }

  // Refresh token
  refreshToken(): Observable<LoginResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const request: RefreshTokenRequest = { refreshToken };
    return this.http.post<LoginResponse>(`${this.API_URL}/refresh`, request)
      .pipe(
        tap(response => this.handleAuthSuccess(response)),
        catchError(error => {
          this.logout();
          return throwError(() => error);
        })
      );
  }

  // Forgot password
  forgotPassword(request: ForgotPasswordRequest): Observable<any> {
    return this.http.post(`${this.API_URL}/forgot-password`, request);
  }

  // Reset password
  resetPassword(request: ResetPasswordRequest): Observable<any> {
    return this.http.post(`${this.API_URL}/reset-password`, request);
  }

  // Verify email
  verifyEmail(token: string): Observable<any> {
    return this.http.post(`${this.API_URL}/verify-email`, { token });
  }

  // Logout
  logout(): void {
    // Clear auth data immediately (optimistic logout)
    this.clearAuthData();

    // Try to notify server, but don't block logout if it fails
    this.http.post(`${this.API_URL}/logout`, {}).subscribe({
      error: (err) => {
        // Silently ignore errors - user is already logged out locally
        console.log('Logout notification to server failed (user already logged out locally):', err);
      }
    });
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  // Get token
  getToken(): string | null {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (!token) {
      console.warn('[AuthService] No token found in localStorage');
    }
    return token;
  }

  // Get refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  // Check if user has role
  hasRole(roles: string[]): boolean {
    const user = this.getCurrentUser();
    return user ? roles.includes(user.role) : false;
  }

  // Private methods
  private handleAuthSuccess(response: LoginResponse): void {
    console.log('[AuthService] Authentication successful, storing tokens');
    localStorage.setItem(this.TOKEN_KEY, response.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, response.refreshToken);
    localStorage.setItem(this.USER_KEY, JSON.stringify(response.user));

    this.currentUserSubject.next(response.user as any);
    this.isAuthenticatedSubject.next(true);

    console.log('[AuthService] User session established:', response.user);
    this.scheduleTokenRefresh();
  }

  private handleAuthError(error: any): Observable<never> {
    console.error('Authentication error:', error);
    return throwError(() => error);
  }

  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);

    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);

    this.router.navigate(['/auth/login']);
  }

  private getStoredUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  private scheduleTokenRefresh(): void {
    // Refresh token every 20 minutes (token expires in 24 hours)
    setTimeout(() => {
      this.refreshToken().subscribe({
        error: () => this.logout()
      });
    }, environment.tokenRefreshInterval);
  }
}
